/* eslint-disable @typescript-eslint/no-explicit-any */

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Gift,
  Headphones,
  Info,
  Loader2,
  Sliders,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useDowngradePlan,
  useGetActiveSubscriptionPlans,
  useGetPlanComparisonData,
  useGetPlanMetrics,
  useUpgradePlan,
} from "@/hooks/use-subscription";
import { cn } from "@/lib/utils";
import {
  b2bPlanObject,
  B2BPlanType,
  b2cPlanObject,
  B2CPlanType,
  PlanCategory,
  PlanPrice,
} from "@/pages/billing/pricing-data";
import {
  DowngradeConfig,
  PaymentIntervalWithoutCustom2,
  useDowngradeStore,
} from "@/stores/downgrade-store";
import {
  DowngradePlanDtoType,
  PaymentInterval,
  SubscriptionStatus,
  UpdatePlanDtoType,
} from "@/types/subscription.types";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { DowngradeConfirmation } from "./downgrade-confirmation";
import { FeatureHighlights, PlanMetricsData } from "./feature-highlights";
import { SeedListFeatureHighlights } from "./seed-list-feature-highlights";
import {
  PlanComparisonTable,
  PlanForComparison,
} from "./plan-comparison-table";
import { ProgressBar } from "./progress-bar";
import { format } from "date-fns";

// Steps in the downgrade flow
const STEPS = {
  CURRENT_PLAN_VALUE: 1,
  PLAN_OPTIMIZATION: 2,
  REVIEW_CHANGES: 3,
  FEEDBACK: 4,
  CONFIRM: 5,
  COMPLETE: 6,
};

// Downgrade reasons
const downgradeReasons = [
  "Too expensive",
  "Not using all features",
  "Found a better alternative",
  "Technical issues",
  "Poor customer support",
  "Business needs changed",
  "Other",
];

// Add state for selected mailboxes in the DowngradeFlow component
export function DowngradeFlow({
  downgradeConfig,
}: {
  downgradeConfig: DowngradeConfig;
}) {
  const [step, setStep] = useState(STEPS.CURRENT_PLAN_VALUE);
  const [selectedPlan, setSelectedPlan] = useState<string | null>("b2c-basic");
  const [showContactSales, setShowContactSales] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [showRetentionAccepted, setShowRetentionAccepted] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  // Add state for selected mailboxes
  const [selectedMailboxes, setSelectedMailboxes] = useState<number[]>([]);

  // Add missing state variables for the feedback section
  const [missedFeatures, setMissedFeatures] = useState<string[]>([]);
  const [otherImprovementDetails, setOtherImprovementDetails] = useState("");
  const [challengesFaced, setChallengesFaced] = useState<string>("");
  const [challengeDetails, setChallengeDetails] = useState("");
  const [recommendationScore, setRecommendationScore] = useState<number | null>(
    null
  );
  const [improvementAreas, setImprovementAreas] = useState<string[]>([]);

  // Add state for billing cycle upgrade
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [upgradeTarget, setUpgradeTarget] = useState<PaymentInterval | null>(
    null
  );
  // Add state for billing cycle confirmation dialog
  const [showBillingCycleConfirmation, setShowBillingCycleConfirmation] =
    useState(false);
  const [pendingBillingCycle, setPendingBillingCycle] =
    useState<PaymentInterval | null>(null);

  // Use the upgrade plan hook
  const { mutateAsync: upgradePlan, isPending: isUpgradePending } =
    useUpgradePlan();

  const { mutateAsync: downgradePlan, isPending: isDowngradePending } =
    useDowngradePlan();

  // Fetch active subscriptions
  const { data: activePlans, isLoading: isLoadingPlans } =
    useGetActiveSubscriptionPlans();

  const { data: planComparisonData, isLoading: isLoadingPlanComparison } =
    useGetPlanComparisonData(
      downgradeConfig?.planId || "",
      downgradeConfig?.targetPlanType || ""
    );

  // Check if this is a seed-list plan
  const isSeedListPlan = downgradeConfig?.planType === "SEED_LIST";

  // Get the specific subscription and plan details
  const subscription = useMemo(
    () =>
      isSeedListPlan
        ? null // Seed list plans might not have a subscription in the same format
        : activePlans?.find((sub) => sub._id === downgradeConfig?.subscriptionId),
    [activePlans, downgradeConfig?.subscriptionId, isSeedListPlan]
  );

  const planDetails = useMemo(
    () =>
      isSeedListPlan
        ? null // Seed list plans handle differently
        : subscription?.plans.find(
            (plan) => plan.planId?._id === downgradeConfig?.planId
          ),
    [subscription, downgradeConfig?.planId, isSeedListPlan]
  );

  // Fetch plan metrics using the API
  const { data: planMetricsResponse, isLoading: isLoadingPlanMetrics } =
    useGetPlanMetrics(downgradeConfig?.planId || "");

  // Process and validate the API response
  const planMetrics = useMemo(() => {
    // Check if response exists and is not an empty object
    if (
      !planMetricsResponse ||
      typeof planMetricsResponse !== "object" ||
      Object.keys(planMetricsResponse).length === 0
    ) {
      return null;
    }

    // Type-cast to our interface
    return planMetricsResponse as unknown as PlanMetricsData;
  }, [planMetricsResponse]);

  // Create a current plan object from the real API data or mock for seed-list
  const currentPlanData: PlanForComparison | null = useMemo(() => {
    // Handle seed-list plans
    if (isSeedListPlan && downgradeConfig) {
      // Calculate price based on seed accounts and sender profiles
      const seedAccounts = downgradeConfig.currentSeedAccounts || 5000;
      const senderProfiles = downgradeConfig.currentSenderProfiles || 5;
      const monthlyPricePerMailbox = 500; // Base price per mailbox/month
      const basePrice = monthlyPricePerMailbox * (seedAccounts / 1000) * senderProfiles;
      
      // Apply discount based on interval
      let intervalMultiplier = 1;
      if (downgradeConfig.currentInterval === PaymentInterval.QUARTERLY) {
        intervalMultiplier = 0.9; // 10% discount
      } else if (downgradeConfig.currentInterval === PaymentInterval.ANNUAL) {
        intervalMultiplier = 0.8; // 20% discount
      } else if (downgradeConfig.currentInterval === PaymentInterval.SEMI_YEARLY) {
        intervalMultiplier = 0.85; // 15% discount
      }
      
      const price = basePrice * intervalMultiplier;

      return {
        type: "SEED_LIST" as any,
        category: "SEED_LIST" as any,
        name: "Gmail Seeds", // Or get from plan name
        price: price,
        billingCycle: downgradeConfig.currentInterval as PaymentInterval,
        features: {
          seedAccounts: seedAccounts,
          senderProfiles: senderProfiles,
          warmupEmails: 0,
          healthTests: 0,
          inboxPlacementTests: 0,
          support: "Email Support",
          api: false,
        },
        startDate: new Date().toISOString(),
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        usage: {
          seedAccounts: Math.floor(seedAccounts * 0.7), // Mock usage
          senderProfiles: Math.floor(senderProfiles * 0.8), // Mock usage
          warmupEmails: 0,
          healthTests: 0,
          inboxPlacementTests: 0,
          apiCalls: 0,
          activeMailboxes: downgradeConfig.count || 0,
        },
      };
    }

    if (!planDetails) return null;

    const category = planDetails.planId?.category as
      | PlanCategory.B2B
      | PlanCategory.B2C;

    const isB2B = category === PlanCategory.B2B;

    const currentPlanFromPricingData = isB2B
      ? b2bPlanObject[planDetails.planId?.planType as B2BPlanType]
      : b2cPlanObject[planDetails.planId?.planType as B2CPlanType];

    if (!currentPlanFromPricingData) return null;
    if (!subscription?.interval) return null;

    const price =
      currentPlanFromPricingData.earlyBirdPrice[
        subscription.interval as keyof PlanPrice
      ];

    return {
      type: planDetails.planId?.category as PlanCategory.B2B | PlanCategory.B2C,
      name: planDetails.planId?.planName || "",
      price: parseFloat(price),
      billingCycle: subscription?.interval as PaymentInterval,
      features: {
        warmupEmails: planDetails.planId?.monthlyWarmupEmails || 0,
        healthTests: planDetails.planId?.monthlyEmailHealthTests || 0,
        inboxPlacementTests:
          planDetails.planId?.monthlyInboxPlacementTests || 0,
        support: currentPlanFromPricingData.limits.support,
        api: currentPlanFromPricingData.limits.api,
      },
      startDate: subscription?.startDate,
      nextBillingDate: subscription?.endDate,
      usage: {
        warmupEmails: planDetails.usedWarmupEmailsMonthly,
        healthTests: planDetails.usedHealthTestsMonthly,
        inboxPlacementTests: planDetails.usedInboxPlacementTestsMonthly,
        apiCalls: 0,
        activeMailboxes: planDetails.assignedMailboxes?.length || 0,
      },
    };
  }, [planDetails, subscription, isSeedListPlan, downgradeConfig]);

  // Create target plan object from API data or mock for seed-list
  const targetPlanData: PlanForComparison | null = useMemo(() => {
    // Handle seed-list plans
    if (isSeedListPlan && downgradeConfig) {
      // For cycle downgrade, use same config with different interval
      if (downgradeConfig.option === "cycle" && downgradeConfig.targetInterval) {
        const seedAccounts = downgradeConfig.currentSeedAccounts || 5000;
        const senderProfiles = downgradeConfig.currentSenderProfiles || 5;
        const monthlyPricePerMailbox = 500;
        const basePrice = monthlyPricePerMailbox * (seedAccounts / 1000) * senderProfiles;
        
        let intervalMultiplier = 1;
        if (downgradeConfig.targetInterval === PaymentInterval.QUARTERLY) {
          intervalMultiplier = 0.9;
        } else if (downgradeConfig.targetInterval === PaymentInterval.ANNUAL) {
          intervalMultiplier = 0.8;
        } else if (downgradeConfig.targetInterval === PaymentInterval.SEMI_YEARLY) {
          intervalMultiplier = 0.85;
        }
        
        const price = basePrice * intervalMultiplier;

        return {
          id: "seed-list",
          name: "Gmail Seeds",
          price: price,
          features: {
            seedAccounts: seedAccounts,
            senderProfiles: senderProfiles,
            warmupEmails: 0,
            healthTests: 0,
            inboxPlacementTests: 0,
            support: "Email Support",
            api: false,
          },
        };
      }
      
      // For config downgrade, use target seed accounts and sender profiles
      if (downgradeConfig.option === "config") {
        const seedAccounts = downgradeConfig.targetSeedAccounts || downgradeConfig.currentSeedAccounts || 5000;
        const senderProfiles = downgradeConfig.targetSenderProfiles || downgradeConfig.currentSenderProfiles || 5;
        const monthlyPricePerMailbox = 500;
        const basePrice = monthlyPricePerMailbox * (seedAccounts / 1000) * senderProfiles;
        
        let intervalMultiplier = 1;
        if (downgradeConfig.currentInterval === PaymentInterval.QUARTERLY) {
          intervalMultiplier = 0.9;
        } else if (downgradeConfig.currentInterval === PaymentInterval.ANNUAL) {
          intervalMultiplier = 0.8;
        } else if (downgradeConfig.currentInterval === PaymentInterval.SEMI_YEARLY) {
          intervalMultiplier = 0.85;
        }
        
        const price = basePrice * intervalMultiplier;

        return {
          id: "seed-list",
          name: "Gmail Seeds",
          price: price,
          features: {
            seedAccounts: seedAccounts,
            senderProfiles: senderProfiles,
            warmupEmails: 0,
            healthTests: 0,
            inboxPlacementTests: 0,
            support: "Email Support",
            api: false,
          },
        };
      }
      
      return null;
    }

    if (!downgradeConfig?.targetPlanType) return null;

    const category = planDetails?.planId?.category as
      | PlanCategory.B2B
      | PlanCategory.B2C;

    const isB2B = category === PlanCategory.B2B;

    const targetPlanFromPricingData = isB2B
      ? b2bPlanObject[downgradeConfig?.targetPlanType as B2BPlanType]
      : b2cPlanObject[downgradeConfig?.targetPlanType as B2CPlanType];

    if (!targetPlanFromPricingData) return null;

    const interval = downgradeConfig?.targetInterval || subscription?.interval;

    const price =
      targetPlanFromPricingData.earlyBirdPrice[interval as keyof PlanPrice];

    return {
      id: downgradeConfig?.targetPlanType,
      name: targetPlanFromPricingData.name,
      price: parseFloat(price),
      features: {
        warmupEmails: targetPlanFromPricingData.limits.warmupEmailsMonthly,
        // healthTests: targetPlanFromPricingData.limits.healthTestsMonthly,
        // inboxPlacementTests:
        //   targetPlanFromPricingData.limits.inboxPlacementTestsMonthly,
        support: targetPlanFromPricingData.limits.support,
        api: targetPlanFromPricingData.limits.api,
      },
    };
  }, [
    downgradeConfig?.targetPlanType,
    planDetails?.planId?.category,
    downgradeConfig?.targetInterval,
    downgradeConfig?.option,
    downgradeConfig?.targetSeedAccounts,
    downgradeConfig?.targetSenderProfiles,
    downgradeConfig?.currentSeedAccounts,
    downgradeConfig?.currentSenderProfiles,
    downgradeConfig?.currentInterval,
    subscription?.interval,
    isSeedListPlan,
  ]);

  useEffect(() => {
    // Set up exit intent detection
    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if they're in the downgrade flow and moving toward the top of the page
      if (
        e.clientY < 50 &&
        step >= STEPS.PLAN_OPTIMIZATION &&
        step < STEPS.COMPLETE &&
        !showExitIntent
      ) {
        setShowExitIntent(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [step, showExitIntent]);

  // Handle opening confirmation dialog for billing cycle change
  const handleBillingCycleChange = (targetInterval: PaymentInterval) => {
    setPendingBillingCycle(targetInterval);
    setShowBillingCycleConfirmation(true);
  };

  // Handle billing cycle upgrade
  const handleUpgradeBillingCycle = async (targetInterval: PaymentInterval) => {
    if (
      !downgradeConfig?.subscriptionId ||
      !downgradeConfig?.planId ||
      !planDetails
    ) {
      toast.error("Missing subscription information");
      return;
    }

    setUpgradeTarget(targetInterval);
    setIsUpgrading(true);

    try {
      // Create payload for upgrading the billing cycle
      const upgradePayload: UpdatePlanDtoType = {
        subscriptionId: downgradeConfig.subscriptionId,
        currentPlanId: downgradeConfig.planId,
        changeType: "billing-cycle",
        newInterval: targetInterval as PaymentIntervalWithoutCustom2,
        quantity: planDetails.quantity,
        assignedMailboxIds: planDetails.assignedMailboxes.map(
          (mailbox) => mailbox.mailboxId
        ),
      };

      // Call the upgrade mutation
      await upgradePlan(upgradePayload);

      toast.success(
        `Successfully switched to ${targetInterval.toLowerCase()} billing`
      );

      window.cioanalytics?.track("plan_upgraded", {
        plan: downgradeConfig.targetPlanType,
        changeType: "billing-cycle",
        newInterval: targetInterval,
        subscriptionId: downgradeConfig.subscriptionId,
        quantity: planDetails.quantity,
        assignedMailboxCount: planDetails.assignedMailboxes.length,
      });
    } catch (error) {
      console.error("Error upgrading plan:", error);
      toast.error("Failed to update billing cycle. Please try again.");
    } finally {
      setIsUpgrading(false);
      setUpgradeTarget(null);
    }
  };

  const handleContinue = () => {
    console.log("handleContinue", step);
    console.log("selectedPlan", selectedPlan);
    if (step === STEPS.CURRENT_PLAN_VALUE) {
      setStep(STEPS.PLAN_OPTIMIZATION);
    } else if (step === STEPS.PLAN_OPTIMIZATION) {
      setStep(STEPS.REVIEW_CHANGES);
    } else if (step === STEPS.REVIEW_CHANGES) {
      setStep(STEPS.FEEDBACK);
    } else if (step === STEPS.FEEDBACK) {
      setStep(STEPS.CONFIRM);
    } else if (step === STEPS.CONFIRM) {
      handleDowngradePlan();
    }
  };

  const handleBack = () => {
    if (step === STEPS.PLAN_OPTIMIZATION) {
      setStep(STEPS.CURRENT_PLAN_VALUE);
    } else if (step === STEPS.REVIEW_CHANGES) {
      setStep(STEPS.PLAN_OPTIMIZATION);
    } else if (step === STEPS.FEEDBACK) {
      setStep(STEPS.REVIEW_CHANGES);
    } else if (step === STEPS.CONFIRM) {
      setStep(STEPS.FEEDBACK);
    }
  };

  const handleDowngradePlan = async () => {
    try {
      if (!downgradeConfig) {
        toast.error("Missing downgrade config");
        return;
      }

      if (!planDetails) {
        toast.error("Missing plan details");
        return;
      }

      const downgradePayload: DowngradePlanDtoType = {
        subscriptionId: downgradeConfig.subscriptionId,
        currentPlanId: downgradeConfig.planId,
        changeType: "plan",
        newPlanType: downgradeConfig.targetPlanType,
        newInterval:
          downgradeConfig.targetInterval as PaymentIntervalWithoutCustom2,
        quantity: planDetails.quantity,
        // assignedMailboxIds: planDetails.assignedMailboxes.map(
        //   (mailbox) => mailbox.mailboxId
        // ),
        reasons: selectedReasons,
        additionalFeedback: feedbackText,
      };

      await downgradePlan(downgradePayload);

      window.cioanalytics?.track("plan_downgraded", {
        plan: downgradeConfig.targetPlanType,
        subscriptionId: downgradeConfig.subscriptionId,
        quantity: planDetails.quantity,
        assignedMailboxCount: planDetails.assignedMailboxes.length,
      });

      setStep(STEPS.COMPLETE);
    } catch (error) {
      console.error("Error downgrading plan:", error);
      toast.error("Failed to downgrade plan. Please try again.");
    }
  };

  const handleContactSales = () => {
    setShowContactSales(true);
  };

  const handleContactSupport = () => {
    setShowSupportDialog(true);
  };

  const handleReasonToggle = (reason: string) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter((r) => r !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  const handleAcceptRetentionOffer = () => {
    setShowRetentionAccepted(true);
  };

  const handleCloseExitIntent = () => {
    setShowExitIntent(false);
  };

  // Add a function to toggle mailbox selection
  const toggleMailboxSelection = (mailboxId: number) => {
    if (selectedMailboxes.includes(mailboxId)) {
      setSelectedMailboxes(selectedMailboxes.filter((id) => id !== mailboxId));
    } else {
      setSelectedMailboxes([...selectedMailboxes, mailboxId]);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case STEPS.CURRENT_PLAN_VALUE:
        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-semibold">
                Your Current {currentPlanData?.name} Plan
              </h2>
              <p className="text-muted-foreground text-sm md:text-base">
                Before considering a plan change, let's review the value you're
                getting from your current plan.
              </p>
            </div>

            {/* Display plan metrics from the API */}
            {isLoadingPlanMetrics && (
              <div className="space-y-4 my-6">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
                <Skeleton className="h-[125px] w-full rounded-md" />
              </div>
            )}

            {/* TODO: Add mailbox selection UI */}
            {/* {currentPlan.type === "b2b" && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">
                  Select Mailboxes to Downgrade
                </h3>
                <p className="text-sm text-muted-foreground">
                  You can choose which mailboxes you want to downgrade. You
                  currently have {currentPlan.mailboxes.length} mailboxes on the{" "}
                  {currentPlan.name} plan.
                </p>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-3 text-left font-medium">
                          <div className="flex items-center">
                            <Checkbox
                              id="select-all"
                              checked={
                                selectedMailboxes.length ===
                                currentPlan.mailboxes.length
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedMailboxes(
                                    currentPlan.mailboxes.map((m) => m.id)
                                  );
                                } else {
                                  setSelectedMailboxes([]);
                                }
                              }}
                              className="mr-2"
                            />
                            <label
                              htmlFor="select-all"
                              className="cursor-pointer"
                            >
                              Mailbox
                            </label>
                          </div>
                        </th>
                        <th className="p-3 text-center font-medium">
                          Daily Emails
                        </th>
                        <th className="p-3 text-center font-medium">
                          Health Tests
                        </th>
                        <th className="p-3 text-center font-medium">
                          Current Usage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {currentPlan.mailboxes.map((mailbox, index) => (
                        <tr
                          key={mailbox.id}
                          className={
                            index % 2 === 0 ? "bg-background" : "bg-muted/30"
                          }
                        >
                          <td className="p-3">
                            <div className="flex items-center">
                              <Checkbox
                                id={`mailbox-${mailbox.id}`}
                                checked={selectedMailboxes.includes(mailbox.id)}
                                onCheckedChange={() =>
                                  toggleMailboxSelection(mailbox.id)
                                }
                                className="mr-2"
                              />
                              <label
                                htmlFor={`mailbox-${mailbox.id}`}
                                className="cursor-pointer"
                              >
                                {mailbox.email}
                              </label>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            {mailbox.usage.warmupEmails}
                          </td>
                          <td className="p-3 text-center">
                            {mailbox.usage.healthTests}
                          </td>
                          <td className="p-3 text-center">
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Active
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-800 font-medium">
                        Important
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        You've selected {selectedMailboxes.length} of{" "}
                        {currentPlanData?.mailboxes.length} mailboxes to downgrade.
                        {selectedMailboxes.length < currentPlanData?.mailboxes.length
                          ? ` The remaining ${
                              currentPlanData?.mailboxes.length -
                              selectedMailboxes.length
                            } mailboxes will stay on your current ${
                              currentPlanData?.name
                            } plan.`
                          : " All mailboxes will be downgraded."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            {currentPlanData?.category === "SEED_LIST" || 
             currentPlanData?.type === "SEED_LIST" ? (
              <SeedListFeatureHighlights
                currentPlan={currentPlanData}
                seedListMetrics={undefined}
                planMetrics={planMetrics || undefined}
              />
            ) : (
              <FeatureHighlights
                currentPlan={currentPlanData}
                planMetrics={planMetrics || undefined}
              />
            )}

            <Alert variant="info">
              <Info className="h-4 w-4  " />
              <AlertTitle>Need help optimizing your plan?</AlertTitle>
              <AlertDescription>
                Our team can help you get the most out of your current plan.
                Schedule a free consultation with your dedicated account
                manager.
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    className=""
                    onClick={() =>
                      window.open(
                        "https://calendly.com/inboxdoctor-sales/new-meeting",
                        "_blank"
                      )
                    }
                  >
                    Schedule Consultation
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            <div className="pt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Link to="/dashboard">
                <Button variant="outline" className="w-full sm:w-auto">
                  <ChevronLeft className="h-4 w-4 " />
                  Return to Dashboard
                </Button>
              </Link>

              <Button
                onClick={handleContinue}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg shadow-sm transition-all"
                // disabled={selectedMailboxes.length === 0}
              >
                Continue to downgrade plan
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        );

      case STEPS.PLAN_OPTIMIZATION:
        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 md:space-y-6"
          >
            <div className="space-y-3 md:space-y-4">
              <div>
                <h2 className="text-lg md:text-xl font-semibold">
                  Optimization Options
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  We have several ways to help you save while keeping your
                  current{" "}
                  {planDetails?.planId?.category || currentPlanData?.type} plan
                  features.
                </p>
              </div>

              <Alert variant="info">
                <Info className="h-4 w-4 " />
                <AlertTitle className="text-sm md:text-base">
                  Stay with Your Current Plan
                </AlertTitle>
                <AlertDescription>
                  <p>
                    With your current{" "}
                    {planDetails?.planId?.planName || currentPlanData?.name}{" "}
                    plan enjoy these benefits :
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    {isSeedListPlan ? (
                      <>
                        <li>
                          Maintain access to{" "}
                          {currentPlanData?.features?.seedAccounts?.toLocaleString() || 0}{" "}
                          seed accounts.
                        </li>
                        <li>
                          Continue using{" "}
                          {currentPlanData?.features?.senderProfiles || 0}{" "}
                          sender profile{currentPlanData?.features?.senderProfiles !== 1 ? "s" : ""}.
                        </li>
                        <li>
                          Keep your support with no interruption to your
                          workflows.
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          Maintain access to{" "}
                          {planMetrics?.dailyAllowance ||
                            currentPlanData?.features?.warmupEmails ||
                            0}{" "}
                          daily warmup emails.
                        </li>
                        <li>
                          Continue using{" "}
                          {planMetrics?.usageMetrics?.healthTests?.total ||
                            currentPlanData?.features?.healthTests ||
                            0}{" "}
                          Email health tests per month.
                        </li>
                        <li>
                          Continue using{" "}
                          {planMetrics?.usageMetrics?.placementTests?.used ||
                            currentPlanData?.features?.inboxPlacementTests ||
                            0}{" "}
                          Email placement tests per month.
                        </li>
                        <li>
                          {currentPlanData?.features?.support && (
                            <li>
                              Keep your support with no interruption to your
                              workflows.
                            </li>
                          )}
                        </li>
                      </>
                    )}
                  </ul>
                  <Button
                    className="mt-3"
                    variant="secondary"
                    size="sm"
                    onClick={() => (window.location.href = "/dashboard")}
                  >
                    Return to Dashboard
                  </Button>
                </AlertDescription>
              </Alert>
            </div>

            <div>
              <h3 className="text-base md:text-lg font-medium">
                Stay on your current plan and save
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                We have several options to help you optimize costs while keeping
                your premium features
              </p>
            </div>

            <div className="space-y-4">
              {subscription &&
                subscription.interval !== PaymentInterval.ANNUAL && (
                  <div className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                      <Calendar className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">Switch to annual billing</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Save 20% by switching to annual billing!
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full sm:w-auto"
                        onClick={() =>
                          handleBillingCycleChange(PaymentInterval.ANNUAL)
                        }
                        disabled={isUpgrading || isUpgradePending}
                      >
                        {isUpgrading &&
                        upgradeTarget === PaymentInterval.ANNUAL ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          "Switch to Annual"
                        )}
                      </Button>
                    </div>
                  </div>
                )}

              {((subscription &&
                subscription.interval === PaymentInterval.MONTHLY) ||
                (isSeedListPlan &&
                  currentPlanData?.billingCycle === PaymentInterval.MONTHLY)) && (
                  <div className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                      <Calendar className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">
                        Switch to quarterly billing
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Save 10% by switching to quarterly billing!
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 w-full sm:w-auto"
                        onClick={() =>
                          handleBillingCycleChange(PaymentInterval.QUARTERLY)
                        }
                        disabled={isUpgrading || isUpgradePending}
                      >
                        {isUpgrading &&
                        upgradeTarget === PaymentInterval.QUARTERLY ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          "Switch to Quarterly"
                        )}
                      </Button>
                    </div>
                  </div>
                )}

              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                  <Headphones className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">
                    Need help optimizing your plan?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Our team can help you get the most out of your current plan.
                    Schedule a free consultation with your dedicated account
                    manager.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full sm:w-auto"
                    onClick={() =>
                      window.open(
                        "https://calendly.com/inboxdoctor-sales/new-meeting",
                        "_blank"
                      )
                    }
                  >
                    Schedule Consultation
                  </Button>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 border rounded-lg">
                <div className="bg-green-100 dark:bg-green-900/20 p-2 rounded-full">
                  <Sliders className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium">Custom plan adjustment</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    We can create a custom plan that fits your exact needs and
                    budget.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full sm:w-auto"
                    onClick={handleContactSales}
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleContinue} className="text-white">
                Continue
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        );

      // case STEPS.SPECIAL_OFFERS:
      //   return (
      //     <motion.div
      //       key={step}
      //       initial={{ opacity: 0, x: 20 }}
      //       animate={{ opacity: 1, x: 0 }}
      //       exit={{ opacity: 0, x: -20 }}
      //       transition={{ duration: 0.3 }}
      //       className="space-y-6"
      //     >
      //       <div className="space-y-4">
      //         <h2 className="text-xl font-semibold">Special Offers</h2>
      //         <p className="text-muted-foreground">
      //           We have special offers for you. Please review them before
      //           continuing.
      //         </p>
      //       </div>

      //       {/* Loading state */}
      //       {isLoadingPlans && (
      //         <div className="space-y-4 my-6">
      //           <Skeleton className="h-[250px] w-full rounded-md" />
      //         </div>
      //       )}

      //       {!isLoadingPlans && (
      //         <Card>
      //           <CardHeader>
      //             <CardTitle>Special Offers</CardTitle>
      //             <CardDescription>
      //               Please review the special offers before continuing.
      //             </CardDescription>
      //           </CardHeader>
      //           <CardContent className="space-y-4">
      //             <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
      //               <h3 className="font-bold text-lg flex items-center text-primary">
      //                 <Gift className="h-5 w-5 mr-2" />
      //                 30% OFF on current plan
      //               </h3>
      //               {/* <p className="text-sm mt-1">
      //                 Stay on your current plan and get 30% off for the next 3
      //                 months - that's a savings of $
      //                 {(currentPlanData?.price || 0) * 0.3 * 3}!
      //               </p> */}
      //             </div>
      //           </CardContent>
      //         </Card>
      //       )}

      //       <div className="pt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
      //         <Button variant="outline" onClick={handleBack}>
      //           <ChevronLeft className="h-4 w-4" />
      //           Back
      //         </Button>
      //         <Button onClick={handleContinue} className="text-white">
      //           <ChevronRight className="h-4 w-4" />
      //           Continue
      //         </Button>
      //       </div>
      //     </motion.div>
      //   );

      case STEPS.REVIEW_CHANGES:
        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-semibold">Review Changes</h2>
              <p className="text-muted-foreground text-sm md:text-base">
                Please review what you'll lose by downgrading from{" "}
                {currentPlanData?.name}.
              </p>
            </div>
            {/* Loading state while fetching plan data */}
            {isLoadingPlans && (
              <div className="space-y-4 my-6">
                <Skeleton className="h-[300px] w-full rounded-md" />
              </div>
            )}
            {/* TODO : select mailbox */}
            {/* {currentApiPlan && currentApiPlan.type === "b2b" && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                <h3 className="font-medium text-blue-800 mb-2">
                  Mailboxes Selected for Downgrade
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  You've selected {selectedMailboxes.length} of{" "}
                  {currentApiPlan.mailboxes.length} mailboxes to downgrade to
                  the {targetApiPlan?.name || selectedPlanDetails?.name} plan.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {currentApiPlan.mailboxes
                    .filter((mailbox: any) =>
                      selectedMailboxes.includes(mailbox.id)
                    )
                    .map((mailbox: any) => (
                      <div
                        key={mailbox.id}
                        className="flex items-center bg-white bg-opacity-60 p-2 rounded"
                      >
                        <Check className="h-4 w-4 text-blue-600 mr-2" />
                        <span className="text-sm">{mailbox.email}</span>
                      </div>
                    ))}
                </div>
                {selectedMailboxes.length < currentApiPlan.mailboxes.length && (
                  <div className="mt-3 text-xs text-blue-600 flex items-center">
                    <Info className="h-4 w-4 mr-1.5" />
                    <span>
                      The remaining{" "}
                      {currentApiPlan.mailboxes.length -
                        selectedMailboxes.length}{" "}
                      mailboxes will stay on your current {currentApiPlan.name}{" "}
                      plan.
                    </span>
                  </div>
                )}
              </div>
            )} */}

            <PlanComparisonTable
              currentPlan={currentPlanData}
              newPlan={targetPlanData}
              usage={currentPlanData?.usage}
              impactData={planComparisonData?.impact}
            />

            {/* TODO: will add it later */}
            {/* <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800">
                Service Impact Warning
              </AlertTitle>
              <AlertDescription className="text-red-700">
                <p className="font-medium">
                  This downgrade will impact your service in the following ways:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>
                    <strong>Warmup Emails:</strong>{" "}
                    {planComparisonData?.impact?.warmupEmailsReduction || 0}{" "}
                    fewer emails per day.
                    <span className="block text-sm mt-0.5">
                      High impact on service
                    </span>
                  </li>
                  <li>
                    <strong>Email Health Tests:</strong>{" "}
                    {planComparisonData?.impact?.healthTestsReduction || 0}{" "}
                    fewer tests per month.
                    <span className="block text-sm mt-0.5">
                      High impact on service
                    </span>
                  </li>
                  <li>
                    <strong>Inbox Placement:</strong> Reduced from 100 to 80.
                    <span className="block text-sm mt-0.5">
                      High impact on deliverability
                    </span>
                  </li>
                  <li>
                    {" "}
                    <strong>Support Level:</strong>{" "}
                    {planComparisonData?.impact?.supportChange
                      ? `Reduced from ${
                          currentPlanData?.features?.support || "Standard"
                        } to ${targetPlanData?.features?.support || "Basic"}`
                      : "No change in support level"}{" "}
                  </li>
                </ul>
              </AlertDescription>
            </Alert> */}

            <Alert variant="info">
              <Info className="h-4 w-4 " />
              <AlertTitle>Need help deciding?</AlertTitle>
              <AlertDescription>
                Our team can help you evaluate if this downgrade is right for
                your needs or find other ways to optimize your costs.
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    className=""
                    onClick={() =>
                      window.open(
                        "https://calendly.com/inboxdoctor-sales/new-meeting",
                        "_blank"
                      )
                    }
                  >
                    Talk to Support
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
            <div className="pt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleContinue} className="text-white">
                Continue
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        );

      case STEPS.FEEDBACK:
        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-xl font-semibold">Help Us Improve</h2>
              <p className="text-muted-foreground text-sm md:text-base">
                We'd like to understand why you're considering a downgrade so we
                can better serve your needs.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium mb-3">
                  Why are you choosing to downgrade your plan?
                </h3>
                <RadioGroup
                  value={selectedReasons[0] || ""}
                  onValueChange={(value) => setSelectedReasons([value])}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cost-too-high" id="cost" />
                    <Label htmlFor="cost" className="font-normal">
                      Cost is too high
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="not-using-features" id="not-using" />
                    <Label htmlFor="not-using" className="font-normal">
                      Not using all features
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="switching-service" id="switching" />
                    <Label htmlFor="switching" className="font-normal">
                      Switching to another service
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="budget-constraints" id="budget" />
                    <Label htmlFor="budget" className="font-normal">
                      Temporary budget constraints
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="other" id="other" />
                    <Label htmlFor="other" className="font-normal">
                      Other
                    </Label>
                  </div>
                </RadioGroup>
                {selectedReasons[0] === "other" && (
                  <Textarea
                    placeholder="Please specify your reason..."
                    className="mt-2"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                  />
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">
                  Are there specific features you'll miss from your current
                  plan?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Higher warmup email limits",
                    "API access",
                    "Dedicated account manager",
                    "Live chat support",
                    "More health/placement tests",
                    "None",
                    "Other",
                  ].map((feature) => (
                    <div
                      key={feature}
                      className={`flex items-center p-3 border rounded-md cursor-pointer ${
                        missedFeatures.includes(feature)
                          ? "border-primary bg-primary/5"
                          : "border-input"
                      }`}
                      onClick={() => {
                        if (feature === "None") {
                          setMissedFeatures(["None"]);
                        } else {
                          setMissedFeatures((prev) =>
                            prev.includes(feature)
                              ? prev.filter((f) => f !== feature)
                              : [...prev.filter((f) => f !== "None"), feature]
                          );
                        }
                      }}
                    >
                      <div
                        className={`w-4 h-4 rounded-sm mr-2 flex items-center justify-center ${
                          missedFeatures.includes(feature)
                            ? "bg-primary"
                            : "border border-input"
                        }`}
                      >
                        {missedFeatures.includes(feature) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span className="font-normal text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                {missedFeatures.includes("Other") && (
                  <Textarea
                    placeholder="Please specify which other features you'll miss..."
                    className="mt-2"
                    value={otherImprovementDetails}
                    onChange={(e) => setOtherImprovementDetails(e.target.value)}
                  />
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">
                  Did you face any challenges using the current plan's features?
                </h3>
                <RadioGroup
                  value={challengesFaced}
                  onValueChange={setChallengesFaced}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="challenges-yes" />
                    <Label htmlFor="challenges-yes" className="font-normal">
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="challenges-no" />
                    <Label htmlFor="challenges-no" className="font-normal">
                      No
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="not-sure" id="challenges-not-sure" />
                    <Label
                      htmlFor="challenges-not-sure"
                      className="font-normal"
                    >
                      Not sure
                    </Label>
                  </div>
                </RadioGroup>
                {challengesFaced === "yes" && (
                  <Textarea
                    placeholder="Please specify the challenges you faced..."
                    className="mt-2"
                    value={challengeDetails}
                    onChange={(e) => setChallengeDetails(e.target.value)}
                  />
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">
                  How likely are you to recommend our service to others? (1-10)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                    <Button
                      key={num}
                      type="button"
                      variant={
                        recommendationScore === num ? "default" : "outline"
                      }
                      className={`h-10 w-10 p-0 font-normal ${
                        recommendationScore === num
                          ? "bg-primary text-primary-foreground"
                          : ""
                      }`}
                      onClick={() => setRecommendationScore(num)}
                    >
                      {num}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium mb-3">
                  What could we improve to better meet your needs?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Lower pricing",
                    "More flexible plans",
                    "Better support",
                    "Additional features",
                    "Easier interface",
                    "Other",
                  ].map((area) => (
                    <div
                      key={area}
                      className={`flex items-center p-3 border rounded-md cursor-pointer ${
                        improvementAreas.includes(area)
                          ? "border-primary bg-primary/5"
                          : "border-input"
                      }`}
                      onClick={() => {
                        setImprovementAreas((prev) =>
                          prev.includes(area)
                            ? prev.filter((a) => a !== area)
                            : [...prev, area]
                        );
                      }}
                    >
                      <div
                        className={`w-4 h-4 rounded-sm mr-2 flex items-center justify-center ${
                          improvementAreas.includes(area)
                            ? "bg-primary"
                            : "border border-input"
                        }`}
                      >
                        {improvementAreas.includes(area) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span className="font-normal text-sm">{area}</span>
                    </div>
                  ))}
                </div>
                {improvementAreas.includes("Other") && (
                  <Textarea
                    placeholder="Please specify what else we could improve..."
                    className="mt-2"
                    value={otherImprovementDetails}
                    onChange={(e) => setOtherImprovementDetails(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div className="pt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between sm:items-center">
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                disabled={!selectedReasons.length}
                className="text-white"
              >
                Continue
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        );

      case STEPS.CONFIRM:
        return (
          <DowngradeConfirmation
            currentPlan={currentPlanData}
            newPlan={targetPlanData}
            onBack={handleBack}
            onConfirm={handleContinue}
            onContactSales={handleContactSales}
            selectedMailboxes={selectedMailboxes}
          />
        );

      case STEPS.COMPLETE:
        return (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="text-center space-y-6 py-8"
          >
            <div className="mx-auto bg-amber-100 rounded-full p-3 w-16 h-16 flex items-center justify-center">
              <Check className="h-8 w-8 text-amber-600" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Downgrade Scheduled</h2>
              <p className="text-muted-foreground">
                Your plan will be downgraded to {targetPlanData?.name} on{" "}
                {currentPlanData?.nextBillingDate
                  ? format(
                      new Date(currentPlanData.nextBillingDate),
                      "MMM d, yyyy"
                    )
                  : "your next billing date"}
                .
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                If you change your mind before{" "}
                {currentPlanData?.nextBillingDate
                  ? format(
                      new Date(currentPlanData.nextBillingDate),
                      "MMM d, yyyy"
                    )
                  : "your next billing date"}
                , you can cancel this downgrade from your dashboard.
              </p>
            </div>
            <div className="pt-4">
              <Button asChild>
                <Link to="/dashboard">Return to Dashboard</Link>
              </Button>
            </div>
          </motion.div>
        );
    }
  };

  if (!downgradeConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold">No downgrade config found</h1>
        <p className="text-muted-foreground">
          Please try again. If the problem persists, please contact support.
        </p>
      </div>
    );
  }

  if (!planDetails && !isSeedListPlan) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold">No plan details found</h1>
        <p className="text-muted-foreground">
          Please try again. If the problem persists, please contact support.
        </p>
      </div>
    );
  }

  if (!currentPlanData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold">No plan data found</h1>
        <p className="text-muted-foreground">
          Please try again. If the problem persists, please contact support.
        </p>
      </div>
    );
  }

  if (subscription?.status === SubscriptionStatus.PENDING) {
    return (
      <main className="flex min-h-screen flex-col p-4 bg-gray-50 dark:bg-background items-center justify-center">
        <Card className="border-none shadow-none bg-transparent max-w-md mx-auto">
          <CardHeader className="space-y-4">
            <div
              className={cn(
                "flex items-center justify-center w-16 h-16 rounded-full mx-auto [&>svg]:size-8",
                "bg-destructive/10 [&>svg]:text-destructive"
              )}
            >
              <X />
            </div>
            <CardTitle className="text-2xl text-center text-foreground">
              Downgrade Not Allowed
            </CardTitle>
            <CardDescription className="text-muted-foreground text-center">
              You cannot downgrade this subscription because it is currently in
              a pending state. This usually means the payment is incomplete or
              the plan hasn't been activated yet. Please wait until the
              subscription becomes active before attempting to downgrade.
            </CardDescription>
          </CardHeader>

          <CardContent className="flex justify-center">
            <Link
              to="/dashboard"
              className={cn(buttonVariants({ variant: "default" }))}
            >
              Back to Dashboard
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <>
      {step !== STEPS.COMPLETE && <ProgressBar currentStep={step} />}
      <Card>
        <CardContent className="p-4 sm:p-6">{renderStepContent()}</CardContent>
      </Card>

      {/* Billing Cycle Confirmation Dialog */}
      <Dialog
        open={showBillingCycleConfirmation}
        onOpenChange={setShowBillingCycleConfirmation}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Billing Cycle Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to switch to{" "}
              {pendingBillingCycle?.toLowerCase()} billing?
              {pendingBillingCycle === PaymentInterval.ANNUAL && (
                <p className="mt-2 text-green-600">
                  You'll save 20% with annual billing!
                </p>
              )}
              {pendingBillingCycle === PaymentInterval.QUARTERLY && (
                <p className="mt-2 text-green-600">
                  You'll save 10% with quarterly billing!
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowBillingCycleConfirmation(false)}
              className="sm:w-auto w-full"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowBillingCycleConfirmation(false);
                if (pendingBillingCycle) {
                  handleUpgradeBillingCycle(pendingBillingCycle);
                }
              }}
              className="sm:w-auto w-full"
              disabled={isUpgrading || isUpgradePending}
            >
              {isUpgrading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                "Confirm Change"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showContactSales} onOpenChange={setShowContactSales}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact Sales Team</DialogTitle>
            <DialogDescription>
              Our sales team will help you find the right plan for your needs
              and budget.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p>Please contact our sales team at:</p>
            <p className="font-medium mt-2">sales@emailwarmup.com</p>
            <p className="font-medium">+1 (555) 123-4567</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                window.open(
                  "https://calendly.com/inboxdoctor-sales/inboxdoctor-custom-plan",
                  "_blank"
                );
                setShowContactSales(false);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSupportDialog} onOpenChange={setShowSupportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule a Consultation</DialogTitle>
            <DialogDescription>
              Our team can help you optimize your current plan to get the most
              value.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p>
              Your dedicated account manager will contact you within 1 business
              day to schedule a consultation.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800">Did you know?</h3>
              <p className="text-sm text-blue-700 mt-1">
                Elite plan customers like you get priority support and
                personalized optimization advice as part of your subscription.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                window.open(
                  "https://calendly.com/inboxdoctor-sales/new-meeting",
                  "_blank"
                );
                setShowSupportDialog(false);
              }}
            >
              Request Consultation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRetentionAccepted}
        onOpenChange={setShowRetentionAccepted}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Offer Accepted!</DialogTitle>
            <DialogDescription>
              We're glad you decided to stay with your current plan.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-medium text-green-800 flex items-center">
                <Check className="h-5 w-5 mr-2" />
                Discount Applied Successfully
              </h3>
              <p className="text-sm text-green-700 mt-1">
                Your 25% discount has been applied to your account. Your next
                billing will reflect this change.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button asChild>
              <Link
                to="/dashboard"
                onClick={() => setShowRetentionAccepted(false)}
              >
                Return to Dashboard
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* <Dialog open={showExitIntent} onOpenChange={setShowExitIntent}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Wait! We have a special offer just for you
            </DialogTitle>
            <DialogDescription>
              We noticed you're considering a downgrade. As a valued customer,
              we'd like to offer you:
            </DialogDescription>
          </DialogHeader>
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/20 my-2">
            <h3 className="font-bold text-lg flex items-center text-primary">
              <Gift className="h-5 w-5 mr-2" />
              30% OFF for 3 months
            </h3>
            <p className="text-sm mt-1">
              Stay on your current plan and get 30% off for the next 3 months -
              that's a savings of $
              {currentPlanData && currentPlanData.price
                ? (currentPlanData.price * 0.3 * 3).toFixed(2)
                : "0.00"}
              !
            </p>
            <div className="text-xs text-muted-foreground mt-2">
              Limited time offer - expires in 24 hours
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleCloseExitIntent}
              className="sm:w-auto w-full"
            >
              No thanks
            </Button>
            <Button
              onClick={handleAcceptRetentionOffer}
              className="sm:w-auto w-full"
            >
              Accept This Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
    </>
  );
}

export const DowngradeFlowValidation = () => {
  const { downgradeConfig } = useDowngradeStore();

  if (!downgradeConfig) {
    return (
      <div className="flex flex-col text-center items-center justify-center h-[calc(100vh-10rem)]">
        <h1 className="text-2xl font-bold">No downgrade config found</h1>
        <p className="text-muted-foreground">
          Please try again. If the problem persists, please contact support.
        </p>

        <Link to="/dashboard" className="mt-4">
          <Button>Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  return <DowngradeFlow downgradeConfig={downgradeConfig} />;
};
